import styled from "@emotion/styled";
import Icon from "@imminently/decisively-ui/Icon";
import Button from "@imminently/decisively-ui/Button";
import Field from "@imminently/decisively-ui/Field";
import TextInput from "@imminently/decisively-ui/TextInput";
import type { TextInputProps } from "@imminently/decisively-ui/TextInput";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Tooltip from "@mui/material/Tooltip";
import copy from "copy-to-clipboard";
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";

export type CopyTextFieldProps = Omit<TextInputProps, "value" | "startAdornment" | "endAdornment"> & {
  value: string;
  label?: ReactNode;
  helperText?: ReactNode;
  required?: boolean;
  error?: boolean;
  /** Ignored legacy MUI prop kept while old call sites are migrated. */
  variant?: string;
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
};

const StyledTextInput = styled(TextInput)`
  font-family: Consolas, monospace;
`;

const popperProps = { disablePortal: false };

export const useCopyValue = (value: string) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [open, setOpen] = useState(false);
  const handleTooltipClose = useCallback(() => setOpen(false), []);

  const maybeClearTimeout = useCallback(() => {
    if (timeoutRef.current === null) return;

    clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
  }, []);

  const copyHandler = useCallback(() => {
    copy(value);
    setOpen(true);

    maybeClearTimeout();
    timeoutRef.current = setTimeout(() => setOpen(false), 1_000);
  }, [value, maybeClearTimeout]);

  useEffect(() => {
    if (open) return;

    maybeClearTimeout();
  }, [open, maybeClearTimeout]);

  return { open, handleTooltipClose, copyHandler };
};

export type CopyIconButtonProps = {
  value: string;
  /** Tooltip to display on copy */
  tooltip?: string;
  icon?: ReactNode;
};

export const CopyIconButton = ({ value, tooltip, icon, ...props }: CopyIconButtonProps) => {
  const { open, handleTooltipClose, copyHandler } = useCopyValue(value);

  return (
    <ClickAwayListener onClickAway={handleTooltipClose}>
      <Tooltip
        PopperProps={popperProps}
        onClose={handleTooltipClose}
        open={open}
        disableFocusListener
        disableHoverListener
        disableTouchListener
        title={tooltip ?? "Copied!"}
        placement="left"
      >
        <Button icon
                size={"sm"}
          type="button"
          onClick={copyHandler}
          {...props}
          tooltip={"false"}
          label="Copy"
        >
          {icon ?? <Icon name="copy" />}
        </Button>
      </Tooltip>
    </ClickAwayListener>
  );
};

export const CopyTextField = ({ value, startAdornment, ...props }: CopyTextFieldProps) => {
  const { open, handleTooltipClose, copyHandler } = useCopyValue(value);
  const { label, helperText, required, error, fullWidth = true, endAdornment, className, variant: _variant, ...rest } = props;

  return (
    <Field
      name={props.name}
      fullWidth={fullWidth}
      className={className}
    >
      {label ? <Field.Label required={required}>{label}</Field.Label> : null}
      <Field.Control>
        <StyledTextInput
          value={value}
          fullWidth={fullWidth}
          error={error}
          startAdornment={startAdornment}
          endAdornment={
            <>
              {endAdornment ? endAdornment : null}
            <ClickAwayListener onClickAway={handleTooltipClose}>
              <Tooltip
                PopperProps={popperProps}
                onClose={handleTooltipClose}
                open={open}
                disableFocusListener
                disableHoverListener
                disableTouchListener
                title="Copied!"
              >
                <Button icon
                  type="button"
                        size={"sm"}
              tooltip={false}
                  onClick={copyHandler}
                  label="Copy"
                >
                  <Icon name="copy" />
                </Button>
              </Tooltip>
            </ClickAwayListener>
            </>
          }
          {...rest}
        />
      </Field.Control>
      <Field.Message
        error={error}
        message={helperText}
      />
    </Field>
  );
};
CopyTextField.displayName = "components/CopyTextField";
